import { Component, Input, Output, OnInit, forwardRef, EventEmitter, ViewChild, SimpleChanges, OnChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

const TAGS = ['start_date', 'end_date'];

@Component({
  selector: 'app-tag-editor',
  templateUrl: './tag-editor.component.html',
  styleUrls: ['./tag-editor.component.sass'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagEditorComponent),
      multi: true
    }
  ]
})
export class TagEditorComponent implements OnInit, ControlValueAccessor {
  @ViewChild('editor') editor;

  @Input() hideButtons = false;
  @Input() disabled = false;
  @Input() placeholder = '';
  @Input() rows = 5;
  @Input() name = '';
  @Input() id = '';
  @Input() required = false;
  // Placeholders the field accepts: the legal terms' dates unless told otherwise.
  @Input() set tags(tags: string[]) {
    this.available = tags || TAGS;
  }
  @Output() contentChange: EventEmitter<any>;
  @Output() blur = new EventEmitter();
  @Output() focus = new EventEmitter();

  available: string[] = TAGS;
  value: string = '';
  content: any = [];

  propagateChange = (_: any) => { };

  constructor(
    private translateService: TranslateService
  ) {
    this.contentChange = new EventEmitter();
  }

  ngOnInit() {
  }

  writeValue(value: any) {
    value = value || '';

    try {
      this.in(value)
    } catch (e) {

    }
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched() { }

  change(content: any) {
    this.propagateChange(content);
    this.contentChange.emit(content);
  }

  public trackById(index: number, item: any) {
    return item.id;
  }

  onContentChange(content: any) {
    this.change(content);
  }

  onBlur(event: any) {
    this.out()

    this.blur.emit(event);
  }

  onFocus(e) {
    this.focus.emit(e);
  }

  onInput(event: any) {
    this.out()
  }

  // A line break, not the <div> the browser opens by default: getText only reads <br> back as a
  // newline.
  onEnter(event: any) {
    event.preventDefault();

    document.execCommand('insertLineBreak');
    this.out();
  }

  onSelect(event: any) {
    const element = event.target;
    const sibling = element.nextSibling;
    const range = document.createRange();
    const sel = window.getSelection();

    range.setStartBefore(sibling);
    range.setEndBefore(sibling);

    sel.removeAllRanges();
    sel.addRange(range);
  }

  onPaste(event: any) {
    event.preventDefault();

    const clipboardData = event.clipboardData;
    const pastedText = clipboardData.getData("text/plain");
    let cleanedText = this.cleanPastedText(pastedText);

    cleanedText = cleanedText.replace(/{{(.*?)}}/g, (match, text) => {
      if (this.available.includes(text.trim())) {
        return this.createBubble(text).outerHTML.trim();
      } else {
        return text;
      }
    });

    cleanedText = cleanedText.replace(/\n/g, '<br>');

    document.execCommand("insertHTML", false, cleanedText);
  }

  onInsert($event: any, text: string) {
    $event.preventDefault();

    const selection: any = window.getSelection();
    const range: any = selection.getRangeAt(0);
    const ancestor: any = range.commonAncestorContainer;
    const parentClass = ['tag-editor-editor', 'tag-editor-text'];

    if ([
      parentClass.includes(ancestor.className) || false,
      parentClass.includes(ancestor.parentNode?.className) || false,
    ].every((v) => v === false)) {
      return;
    }

    range.deleteContents();

    const node = this.createBubble(text);

    range.insertNode(node);

    const range2 = document.createRange();
    const sel = window.getSelection();

    range2.setStartBefore(node.nextSibling);
    range2.setEndBefore(node.nextSibling);

    sel.removeAllRanges();
    sel.addRange(range2);

    this.out();
  }

  getSelected() {
    if (window.getSelection) {
      return window.getSelection();
    } else if (document.getSelection) {
      return document.getSelection();
    }
  }

  private in(text: string) {
    const $editor = this.editor.nativeElement;
    const splits = text.split(/({{.*?}})/g);

    this.value = text;
    $editor.innerHTML = '';

    splits.forEach((split) => {
      if (split.match(/{{.*?}}/g)) {
        const tag = split.replace('{{', '').replace('}}', '').trim();

        if (this.available.includes(tag)) {
          $editor.appendChild(this.createBubble(tag));
        } else if (split) {
          $editor.appendChild(document.createTextNode(split));
        }
      } else {
        if (split) {
          const newlines = split.split(/\n/g);
          const last = newlines.length

          newlines.forEach((newlineText, index) => {
            $editor.appendChild(document.createTextNode(newlineText));

            if (index + 1 < last) {
              $editor.appendChild(document.createElement('br'));
            }
          })
        }
      }
    })
  }

  private out() {
    const children = this.editor.nativeElement.childNodes;
    const content = this.getText(children).trim()

    this.value = content

    this.propagateChange(content);
    this.contentChange.emit(content);
  }

  private createBubble(text: string) {
    let node = document.createElement('span');
    node.textContent = this.translateService.instant('shared.tags.' + text.trim());
    node.className = 'tag-editor-bubble';
    node.dataset.tag = text.trim();
    node.contentEditable = 'false';

    return node;
  }

  private cleanPastedText(text: string) {
    const div = document.createElement("div");
    div.innerHTML = text

    return div.textContent;
  }

  private getText(children: NodeListOf<ChildNode>) {
    let text = '';

    children.forEach((child: HTMLElement) => {
      if (child.className === 'tag-editor-bubble') {
        text += '{{' + child.dataset.tag + '}}';
      } else if (child.nodeName === 'BR') {
        text += '\n';
      } else if (child.nodeType === 3 && child.textContent) {
        text += child.textContent;
      } else if (child.childNodes.length) {
        text += this.getText(child.childNodes);
      }
    })

    return text;
  }
}
